import { useState, useRef } from "react";
import { TextField } from "@fluentui/react/lib/TextField";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Callout } from "@fluentui/react/lib/Callout";
import { ChatAppQuestion, ChatAppResponse, MessageFeedback } from "../../api";
import { mergeStyleSets, Stack } from "@fluentui/react";
import { ThumbDislike20Regular, ThumbLike20Regular } from "@fluentui/react-icons";

import styles from "./Answer.module.css";

interface Props {
    questionAnswerPair: [user: ChatAppQuestion, response: ChatAppResponse];
    isStreaming: boolean;
    hasPositiveFeedback?: boolean;
    hasNegativeFeedback?: boolean;
    onFeedbackSent: (messageFeedback: MessageFeedback) => void;
}

export const AnswerFeedback = ({ questionAnswerPair, isStreaming, hasPositiveFeedback, hasNegativeFeedback, onFeedbackSent }: Props) => {
    const [isThumbUp, setIsThumbUp] = useState<boolean>(hasPositiveFeedback ? hasPositiveFeedback : false);
    const [isThumbDown, setIsThumbDown] = useState<boolean>(hasNegativeFeedback ? hasNegativeFeedback : false);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [comment, setComment] = useState("");

    const thumbUpRef = useRef(null);
    const thumbDownRef = useRef(null);
    const [calloutTarget, setCalloutTarget] = useState<any>(null);

    const giveFeedback = (isPositive: boolean) => {
        if (!isStreaming) {
            if (isPositive) {
                setIsThumbDown(false);
                // Done to avoid useless call to BE. Otherwise the api would be called n times when clicking always on the same button
                if (!isThumbUp) {
                    setIsThumbUp(true);
                    setCalloutTarget(thumbUpRef);
                    setIsPopupVisible(true);
                }
            } else {
                setIsThumbUp(false);
                if (!isThumbDown) {
                    setIsThumbDown(true);
                    setCalloutTarget(thumbDownRef);
                    setIsPopupVisible(true);
                }
            }
        }
    };

    const handleSend = async () => {
        setIsPopupVisible(false);
        // create the feedback
        let feedback = {
            conversationId: questionAnswerPair[1].session_state,
            answerDialogId: questionAnswerPair[1].dialogId,
            question: questionAnswerPair[0].message,
            answer: questionAnswerPair[1].message.content,
            isPositive: isThumbUp, // if thumb up then positive, else is negative.
            comment: comment
        } as MessageFeedback;

        await onFeedbackSent(feedback);
        // clean the comment
        setComment("");
    };

    return (
        <Stack.Item>
            {/* User feedback to answers */}
            <Stack horizontal horizontalAlign="stretch" style={{ position: "relative", paddingTop: "20px", display: "inline-block" }}>
                <span> Was this answer useful? </span>
                <ThumbLike20Regular style={{ color: isThumbUp ? "#107c10" : "#1f1f1f", cursor: "pointer" }} onClick={() => giveFeedback(true)} ref={thumbUpRef} />
                <ThumbDislike20Regular
                    style={{ color: isThumbDown ? "#ff000f" : "#1f1f1f", cursor: "pointer" }}
                    onClick={() => giveFeedback(false)}
                    ref={thumbDownRef}
                />
                {isPopupVisible && (
                    <Callout
                        className={styles.feedbackCallout}
                        target={calloutTarget}
                        onDismiss={() => setIsPopupVisible(false)}
                        styles={{
                            root: {
                                borderRadius: 8,
                                overflow: "hidden",
                                padding: 0
                            }
                        }}
                        setInitialFocus
                    >
                        <TextField
                            className={styles.calloutTextField}
                            label="Add a comment (optional)"
                            multiline
                            rows={3}
                            value={comment}
                            onChange={(e, newValue) => setComment(newValue || "")}
                        />
                        <PrimaryButton
                            className={styles.sendFeedback}
                            text="Send"
                            onClick={handleSend}
                            styles={{
                                root: { backgroundColor: "#ff000f", borderColor: "#ff000f", borderRadius: 4 },
                                rootHovered: { backgroundColor: "#cc0000", borderColor: "#cc0000" },
                                rootPressed: { backgroundColor: "#aa0000", borderColor: "#aa0000" }
                            }}
                        />
                    </Callout>
                )}
            </Stack>
        </Stack.Item>
    );
};

export default AnswerFeedback;
